import { useQuery, useLazyQuery } from '@apollo/client';
import {
  ALL_MODULES,
  AsyncPropSelector,
  CUSTOM_FIELDS,
  T1,
  getTranslatedText
} from '@nile/components';
import { GET_SECURE_FIELD_VALUE, AccountClient } from '@nile/graphql';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import {
  Popover,
  TextField,
  Button,
  ButtonGroup
} from '@superops/ui-design-system';
import { Edit } from '@styled-icons/material/Edit';
import { cloneDeep, isEqual } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { EditIcon } from '@nile/components/src/Icons';

const CustomField = styled.div`
  min-width: 200px;
  max-width: 250px;
  flex-grow: 1;
  padding-right: 10px;
  padding-left: 10px;
  margin-top: 8px;
  margin-bottom: 8px;
  .Polaris-ButtonGroup {
    margin-top: 12px;
  }
`;

const Label = styled.div`
  text-align: left;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 10px;
`;

const Wrap = styled.div`
  display: flex;
  align-items: center;
  .edit-actions {
    visibility: hidden;
    cursor: pointer;
  }
  padding-left: 8px;
  padding-right: 8px;
  max-width: 100%;
`;

const CField = styled.div`
  height: 100%;
  max-height: 40px;
  display: flex;
  align-items: center;
  .editable-cell,
  .cell {
    flex-grow: 1;
    font-weight: bold;
  }
  &:hover {
    .edit-actions {
      visibility: visible;
    }
  }
`;

function SecureText(props) {
  const {
    field,
    ticket,
    defaultValue,
    updateSelection,
    editable,
    value,
    inlineEdit,
    disableSelection,
    entityId,
    isMandatory,
    error
  } = props;

  const intl = useIntl();

  const [inputType, setInputType] = useState(!value ? 'text' : 'password');
  const [updatedValue, setUpdatedValue] = useState(value || '');
  const updatedValueByRef = React.useRef(null);
  updatedValueByRef.current = updatedValue;

  const [viewing, setViewing] = useState(false);
  const [
    getSecureFieldValue,
    {
      data: secureFieldValue,
      loading: secureFieldLoading,
      error: secureFieldError
    }
  ] = useLazyQuery(GET_SECURE_FIELD_VALUE, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted(res) {
      if (res?.getSecureFieldValue) {
        if (inlineEdit) {
          setUpdatedValue(res?.getSecureFieldValue);
        } else {
          updateSelection(res?.getSecureFieldValue);
        }
        setInputType('text');
      }
    }
  });
  const [
    getSecureFieldViewValue,
    {
      data: secureFieldViewValue,
      loading: secureFieldViewLoading,
      error: secureFieldViewError
    }
  ] = useLazyQuery(GET_SECURE_FIELD_VALUE, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    onCompleted(res) {
      if (res?.getSecureFieldValue) {
        if (inlineEdit) {
          setUpdatedValue(res?.getSecureFieldValue);
        } else {
          updateSelection(res?.getSecureFieldValue);
        }
        setViewing(true);
      }
    }
  });

  const updateSelectedField = (val) => {
    const parsedVal = val;
    setUpdatedValue(parsedVal);
    if (updateSelection) {
      updateSelection(field, parsedVal);
    }
  };
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => {
    setPopoverActive(!popoverActive);
    setInputType(!value ? 'text' : 'password');
  });

  const updateUpdatedValue = (val) => {
    setUpdatedValue(val);
    if (updateSelection) {
      updateSelection(field, val);
    }
  };
  const getField = () => {
    const type = field.fieldType || field.uiDataType;
    switch (type) {
      case 'secure_text':
      case CUSTOM_FIELDS.secureText:
        return (
          <div
            className={`flex center ${secureFieldLoading ? 'disabled' : ''}`}
          >
            <TextField
              onChange={inlineEdit ? updateUpdatedValue : updateSelectedField}
              value={updatedValueByRef.current}
              placeholder={getTranslatedText(
                intl,
                'common.placeholder.enterValue'
              )}
              disabled={disableSelection || inputType === 'password'}
              type={inputType}
              error={error}
            />
            <div
              className="ml8 pointer flex center"
              onClick={() => {
                if (inputType === 'password') {
                  if (
                    (value === updatedValue && inlineEdit) || // for creation forms exclusion
                    (!inlineEdit && value === '*******') // for edit form inclusion
                  ) {
                    getSecureFieldValue({
                      variables: {
                        input: {
                          module: props.module,
                          entityId,
                          customFieldName: field.columnName || field.name
                        }
                      }
                    });
                  } else {
                    setInputType('text');
                  }
                } else {
                  setInputType('password');
                }
              }}
            >
              <SVGIcon
                name={inputType === 'password' ? 'EyeHidden' : 'EyeVisible'}
              />
            </div>
          </div>
        );
      default:
    }
  };
  const activator = <EditIcon onClick={() => togglePopoverActive()} />;

  useEffect(() => {
    setInputType(!value ? 'text' : 'password');
    setCurrentValue(value);
  }, [value]);

  const getTag = () => {
    return (
      <CustomField className="cf-wrapper">
        <Label className="cf-label">
          {field.label || ''}{' '}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </Label>
        {getField()}
        <div className="mt10">
          <ButtonGroup>
            <Button
              onClick={() => {
                togglePopoverActive();
                setUpdatedValue(value || '');
                setViewing(false);
              }}
            >
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              onClick={() => {
                updateSelectedField(updatedValue);
                togglePopoverActive();
                setViewing(false);
              }}
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        </div>
      </CustomField>
    );
  };
  const displayVal = value;
  // if (loading) return <div>loading...</div>;
  if (inlineEdit) {
    if (viewing && !popoverActive) {
      return (
        <CField>
          <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
            <div className="ellipsis" title={displayVal}>
              {updatedValue}
            </div>
            <div
              className="ml8 pointer flex center"
              onClick={() => {
                setViewing(false);
              }}
            >
              <SVGIcon name="EyeVisible" />
            </div>
            {editable && <div className="edit-actions">{activator}</div>}
          </Wrap>
        </CField>
      );
      // eslint-disable-next-line no-else-return
    } else if (popoverActive) {
      return (
        <CField>
          <Wrap className="editable-cell">
            {viewing && (
              <div className="ellipsis" title={displayVal}>
                {updatedValue}
              </div>
            )}
            {!viewing && (
              <div className="ellipsis" title={displayVal}>
                {displayVal ? '*******' : <DisabledTag>NA</DisabledTag>}
              </div>
            )}
            <Popover
              activator={activator}
              active={popoverActive}
              onClose={() => {
                // togglePopoverActive()
              }}
            >
              {getTag()}
            </Popover>
          </Wrap>
        </CField>
      );
      // eslint-disable-next-line no-else-return
    } else {
      return (
        <CField className={`${secureFieldViewLoading ? 'disabled' : ''}`}>
          <Wrap className={`${editable ? 'editable-cell' : 'cell'}`}>
            <div className="ellipsis" title={displayVal}>
              {displayVal ? '*******' : <DisabledTag>NA</DisabledTag>}
            </div>
            {displayVal && (
              <div
                className="ml8 pointer flex center"
                onClick={() => {
                  getSecureFieldViewValue({
                    variables: {
                      input: {
                        module: props.module,
                        entityId,
                        customFieldName: field.columnName || field.name
                      }
                    }
                  });
                }}
              >
                <SVGIcon name="EyeHidden" />
              </div>
            )}
            {editable && <div className="edit-actions">{activator}</div>}
          </Wrap>
        </CField>
      );
    }
  }
  return (
    <CustomField className="cf-wrapper">
      <Label className="cf-label flex">
        <T1>{field.label || ''}</T1>
        {isMandatory && (
          <>
            <span className="mandatory-star">*</span>
          </>
        )}
      </Label>
      <div className="flex col" key={field.name}>
        {getField()}
      </div>
    </CustomField>
  );
}

export default SecureText;
